import { FC } from 'react';
import { Text } from '../Text';
import SpecSubtitle from '../SpecSubtitle';
import { useTranslation, useRTL } from '../../utils/translations';
import { uimetadata } from '../../utils/metadata';
import { arePricesHiddenForCountry } from '../../utils/pricing';
import { useSelectedJourney, getJourneySpecs } from '../../utils/selectedJourney';

interface SpecDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  onBookingClick?: () => void;
  onShareClick?: () => void;
  showPrice?: boolean;
  modelId?: string;
}

const SpecDetails: FC<SpecDetailsProps> = ({
  isOpen,
  onClose,
  onBookingClick,
  onShareClick,
  showPrice,
  modelId: modelIdProp,
}) => {
  const { currentLanguage } = useTranslation();
  const { isRTL } = useRTL();

  // Resolve modelId without SDK — prefer prop, then config, then first available model key
  const candidateIds = [
    modelIdProp,
    (uimetadata as any).config?.model,
    'Traverse',
  ].filter(Boolean) as string[];
  const modelId =
    candidateIds.find(
      (id) =>
        (uimetadata.models as unknown as Record<string, unknown>)?.[id] !==
        undefined
    ) || 'Traverse';

  // string[] renders as a bulleted list (see SpecSubtitle) - needed for
  // colour lists, which cannot be comma-joined safely in Arabic.
  type LabelMap = Record<string, string | string[]>;
  type SummaryItem = {
    title: LabelMap;
    subtitle: LabelMap;
    price?: { default: number };
  };
  type SpecificationsMeta = {
    heading?: LabelMap;
    price?: LabelMap;
    model?: string;
    trim?: LabelMap;
    seatingCapacity?: string;
    dimensions?: {
      length?: string;
      width?: string;
      height?: string;
      wheelbase?: string;
      groundClearance?: string;
    };
    summary?: SummaryItem[];
  };
  type ModelMeta = {
    specifications?: SpecificationsMeta;
  };

  const modelMeta =
    (uimetadata.models?.[modelId] as unknown as ModelMeta) || {};
  // Reflect the selected journey (Stingray / GS X) in the configurator spec panel too.
  const journey = useSelectedJourney();
  const specs = (getJourneySpecs(journey) as SpecificationsMeta) ?? modelMeta.specifications;

  // Safe access — featureFlags may not be present in all builds
  // No price at all on a no-pricing country link (LBP / IQD).
  const showPriceFlag =
    !arePricesHiddenForCountry() &&
    ((uimetadata as any).featureFlags?.showPriceInPresentation ?? false);

  if (!isOpen) return null;

  return (
    <>
      {/* Desktop: Side Sheet (>= md) */}
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className={`hidden md:flex flex-col fixed top-0 h-[100dvh] z-[10001] w-[700px] bg-black/80 backdrop-blur-md text-white transform transition-transform duration-300 ease-in-out ${
          isRTL ? 'right-0' : 'left-0'
        } translate-x-0`}
      >
        <div className="h-full flex flex-col p-8 relative">
          <button
            onClick={onClose}
            aria-label="Close"
            className={`absolute top-6 text-white/70 hover:text-white ${
              isRTL ? 'left-6' : 'right-6'
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className={`mb-12 mt-4 ${isRTL ? 'mr-1' : 'ml-1'} flex-shrink-0`}
          >
            <Text
              as="h2"
              className="text-3xl font-bold tracking-wide uppercase mb-1"
            >
              {specs?.heading?.[currentLanguage] || specs?.heading?.en || 'N/A'}
            </Text>
            {(showPrice ?? showPriceFlag) && (
              <Text as="p" className="text-lg text-white/90 font-light">
                {specs?.price?.[currentLanguage] || specs?.price?.en || 'N/A'}
              </Text>
            )}
          </div>

          <div
            className="space-y-6 flex-1 overflow-y-auto min-h-0 pr-6"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
          >
            {specs?.summary?.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-baseline border-b border-white/10 pb-4"
              >
                <Text as="span" className="text-[12px] font-light w-1/3">
                  {item.title[currentLanguage] || item.title.en || 'N/A'}
                </Text>
                <SpecSubtitle
                  className="text-[10px] text-white/70 font-light w-2/3"
                  value={item.subtitle[currentLanguage]}
                  fallback={item.subtitle.en}
                  isRTL={isRTL}
                />
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex-shrink-0">
            <button
              onClick={onBookingClick}
              className="w-full bg-white/20 hover:bg-white/30 text-white py-3 rounded backdrop-blur-sm transition-colors uppercase tracking-wider text-sm"
            >
              <Text
                as="span"
                translationKey="staticTranslations.bottomBar.leads"
                fallback="Save"
              />
            </button>
          </div>

          <button
            onClick={onShareClick}
            className="mt-6 flex items-center text-white/70 hover:text-white cursor-pointer w-fit bg-transparent border-none p-0 flex-shrink-0"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <Text
              as="span"
              translationKey="staticTranslations.specDetails.share"
              fallback="Share"
            />
          </button>
        </div>
      </div>

      {/* Mobile: Bottom Sheet (< md) */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-[10001] bg-black/90 backdrop-blur-md text-white transform translate-y-0 transition-transform duration-300 ease-in-out rounded-t-3xl"
        style={{ height: '85dvh' }}
      >
        <div className="h-full flex flex-col p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Handle bar */}
          <div className="w-12 h-1 bg-white/30 rounded-full mx-auto mb-4 flex-shrink-0" />

          <button
            onClick={onClose}
            aria-label="Close"
            className={`absolute top-6 text-white/70 hover:text-white ${
              isRTL ? 'left-6' : 'right-6'
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="mb-8 mt-2 flex-shrink-0">
            <Text
              as="h2"
              className="text-2xl font-bold tracking-wide uppercase mb-1"
            >
              {specs?.heading?.[currentLanguage] || specs?.heading?.en || 'N/A'}
            </Text>
            {(showPrice ?? showPriceFlag) && (
              <Text as="p" className="text-base text-white/90 font-light">
                {specs?.price?.[currentLanguage] || specs?.price?.en || 'N/A'}
              </Text>
            )}
          </div>

          <div
            className="space-y-4 flex-1 overflow-y-auto min-h-0 pr-6"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
          >
            {specs?.summary?.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-baseline border-b border-white/10 pb-3"
              >
                <Text as="span" className="text-[12px] font-light w-1/3">
                  {item.title[currentLanguage] || item.title.en || 'N/A'}
                </Text>
                <SpecSubtitle
                  className="text-[10px] text-white/70 font-light w-2/3"
                  value={item.subtitle[currentLanguage]}
                  fallback={item.subtitle.en}
                  isRTL={isRTL}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex-shrink-0">
            <button
              onClick={onBookingClick}
              className="w-full bg-white/20 hover:bg-white/30 text-white py-3 rounded backdrop-blur-sm transition-colors uppercase tracking-wider text-sm"
            >
              <Text
                as="span"
                translationKey="staticTranslations.bottomBar.leads"
                fallback="Save"
              />
            </button>
          </div>

          <button
            onClick={onShareClick}
            className="mt-4 flex items-center justify-center text-white/70 hover:text-white cursor-pointer w-full bg-transparent border-none p-0 flex-shrink-0"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <Text
              as="span"
              translationKey="staticTranslations.specDetails.share"
              fallback="Share"
            />
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[10000]"
        onClick={onClose}
        aria-hidden="true"
      />
    </>
  );
};

export default SpecDetails;
